import type { StateCreator } from 'zustand'

export interface SelectionTarget {
  modelId: string
  localId: number
}

export interface FloatingRect {
  x: number
  y: number
  w: number
  h: number
}

export interface ContextMenuState {
  /** Viewport-relative pixel position of the menu's anchor (cursor). */
  x: number
  y: number
  /** The element under the right-click cursor, if any. Null = clicked
   *  on empty canvas. The caller has already updated the selection
   *  before opening the menu (right-click on an unselected element
   *  replaces the selection with that single element). */
  target: SelectionTarget | null
}

export interface SelectionSlice {
  selection: SelectionTarget | null
  // Full multi-selection list (includes the primary above as the
  // last entry). Used for highlighting many elements and for the
  // properties panel summary view.
  selectedTargets: SelectionTarget[]
  // Whatever element is currently under the cursor. Used by the
  // status bar and tooltip; also painted by the highlighter as a
  // soft hover overlay.
  hover: SelectionTarget | null
  // Last cursor pixel coords inside the canvas; tooltip uses this.
  hoverCursor: { x: number; y: number } | null
  // Marquee (window/crossing) selection toggle.
  marqueeMode: boolean
  rightSidebarWidth: number
  rightSidebarCollapsed: boolean
  leftSidebarWidth: number
  leftSidebarCollapsed: boolean
  rawIfcEnabled: boolean
  // Browser-style history of selections so the user can drill into an
  // inverse relation target and go back to the previous element. The
  // current entry sits at history[historyIndex]; entries past
  // historyIndex represent the "forward" stack.
  selectionHistory: SelectionTarget[]
  historyIndex: number
  rightSidebarMode: 'fixed' | 'floating' | 'window'
  rightSidebarFloating: FloatingRect
  contextMenu: ContextMenuState | null

  setSelection: (target: SelectionTarget | null) => void
  addToSelection: (target: SelectionTarget) => void
  removeFromSelection: (target: SelectionTarget) => void
  toggleSelection: (target: SelectionTarget) => void
  applyBulkSelection: (
    targets: SelectionTarget[],
    mode: 'replace' | 'add' | 'remove',
  ) => void
  clearSelection: () => void
  setHover: (target: SelectionTarget | null) => void
  setHoverCursor: (cursor: { x: number; y: number } | null) => void
  setMarqueeMode: (v: boolean) => void
  setRightSidebarWidth: (width: number) => void
  setRightSidebarCollapsed: (collapsed: boolean) => void
  setLeftSidebarWidth: (width: number) => void
  setLeftSidebarCollapsed: (collapsed: boolean) => void
  setRawIfcEnabled: (v: boolean) => void
  goBackSelection: () => SelectionTarget | null
  goForwardSelection: () => SelectionTarget | null
  clearSelectionHistory: () => void
  setRightSidebarMode: (mode: 'fixed' | 'floating' | 'window') => void
  setRightSidebarFloating: (rect: FloatingRect) => void
  openContextMenu: (state: ContextMenuState) => void
  closeContextMenu: () => void
}

function eqTarget(
  a: SelectionTarget | null,
  b: SelectionTarget | null,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.modelId === b.modelId && a.localId === b.localId
}

function dedupeTargets(targets: SelectionTarget[]): SelectionTarget[] {
  const seen = new Set<string>()
  const out: SelectionTarget[] = []
  for (const t of targets) {
    const key = `${t.modelId}:${t.localId}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(t)
  }
  return out
}

export const RIGHT_SIDEBAR_MIN = 280
export const RIGHT_SIDEBAR_MAX = 720
export const RIGHT_SIDEBAR_DEFAULT = 380

export const LEFT_SIDEBAR_MIN = 200
export const LEFT_SIDEBAR_MAX = 640
export const LEFT_SIDEBAR_DEFAULT = 288

const RAW_IFC_ENABLED_KEY = 'vn2r.rawIfcEnabled'
const RSB_DETACHED_KEY = 'vn2r.rightSidebarDetached'
const RSB_FLOATING_KEY = 'vn2r.rightSidebarFloating'
const RSB_COLLAPSED_KEY = 'vn2r.rightSidebarCollapsed'
const LSB_WIDTH_KEY = 'vn2r.leftSidebarWidth'
const LSB_COLLAPSED_KEY = 'vn2r.leftSidebarCollapsed'

function loadRightSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(RSB_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

function loadLeftSidebarWidth(): number {
  try {
    const v = localStorage.getItem(LSB_WIDTH_KEY)
    if (v === null) return LEFT_SIDEBAR_DEFAULT
    const n = Number(v)
    if (!Number.isFinite(n)) return LEFT_SIDEBAR_DEFAULT
    return Math.min(LEFT_SIDEBAR_MAX, Math.max(LEFT_SIDEBAR_MIN, n))
  } catch {
    return LEFT_SIDEBAR_DEFAULT
  }
}

function loadLeftSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(LSB_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

function loadRawIfcEnabled(): boolean {
  try {
    const v = localStorage.getItem(RAW_IFC_ENABLED_KEY)
    if (v === null) return true
    return v === 'true'
  } catch {
    return true
  }
}

function persistRawIfcEnabled(v: boolean) {
  try {
    localStorage.setItem(RAW_IFC_ENABLED_KEY, String(v))
  } catch {
    /* ignore */
  }
}

function loadMode(): 'fixed' | 'floating' | 'window' {
  try {
    const v = localStorage.getItem(RSB_DETACHED_KEY)
    if (v === 'floating' || v === 'window' || v === 'fixed') return v
    // Legacy boolean (true => floating).
    if (v === 'true') return 'floating'
    return 'fixed'
  } catch {
    return 'fixed'
  }
}

function persistMode(mode: 'fixed' | 'floating' | 'window') {
  try {
    localStorage.setItem(RSB_DETACHED_KEY, mode)
  } catch {
    /* ignore */
  }
}

function defaultFloatingRect(): FloatingRect {
  const w = 420
  const h = 700
  const x =
    typeof window !== 'undefined'
      ? Math.max(40, window.innerWidth - w - 40)
      : 40
  const y = 60
  return { x, y, w, h }
}

function loadFloating(): FloatingRect {
  try {
    const raw = localStorage.getItem(RSB_FLOATING_KEY)
    if (!raw) return defaultFloatingRect()
    const parsed = JSON.parse(raw) as Partial<FloatingRect>
    const def = defaultFloatingRect()
    return {
      x: typeof parsed.x === 'number' ? parsed.x : def.x,
      y: typeof parsed.y === 'number' ? parsed.y : def.y,
      w: typeof parsed.w === 'number' ? parsed.w : def.w,
      h: typeof parsed.h === 'number' ? parsed.h : def.h,
    }
  } catch {
    return defaultFloatingRect()
  }
}

function persistFloating(rect: FloatingRect) {
  try {
    localStorage.setItem(RSB_FLOATING_KEY, JSON.stringify(rect))
  } catch {
    /* ignore */
  }
}

export const createSelectionSlice: StateCreator<SelectionSlice> = (
  set,
  get,
) => ({
  selection: null,
  selectedTargets: [],
  hover: null,
  hoverCursor: null,
  marqueeMode: false,
  rightSidebarWidth: RIGHT_SIDEBAR_DEFAULT,
  rightSidebarCollapsed: loadRightSidebarCollapsed(),
  leftSidebarWidth: loadLeftSidebarWidth(),
  leftSidebarCollapsed: loadLeftSidebarCollapsed(),
  rawIfcEnabled: loadRawIfcEnabled(),
  selectionHistory: [],
  historyIndex: -1,
  rightSidebarMode: loadMode(),
  rightSidebarFloating: loadFloating(),
  contextMenu: null,

  setSelection: (target) => {
    const state = get()
    const cur = state.selectionHistory[state.historyIndex] ?? null
    if (eqTarget(target, cur)) {
      // Echo from goBack/goForward.
      set({
        selection: target,
        selectedTargets: target ? [target] : [],
      })
      return
    }
    if (!target) {
      set({ selection: null, selectedTargets: [] })
      return
    }
    const truncated = state.selectionHistory.slice(0, state.historyIndex + 1)
    truncated.push(target)
    set({
      selection: target,
      selectedTargets: [target],
      selectionHistory: truncated,
      historyIndex: truncated.length - 1,
    })
  },
  addToSelection: (target) => {
    const state = get()
    const exists = state.selectedTargets.some((t) => eqTarget(t, target))
    const next = exists
      ? state.selectedTargets
      : [...state.selectedTargets, target]
    // Make this the primary, advancing history only if it differs
    // from the current primary.
    const cur = state.selectionHistory[state.historyIndex] ?? null
    if (eqTarget(target, cur)) {
      set({ selection: target, selectedTargets: next })
      return
    }
    const truncated = state.selectionHistory.slice(0, state.historyIndex + 1)
    truncated.push(target)
    set({
      selection: target,
      selectedTargets: next,
      selectionHistory: truncated,
      historyIndex: truncated.length - 1,
    })
  },
  removeFromSelection: (target) => {
    const state = get()
    const next = state.selectedTargets.filter((t) => !eqTarget(t, target))
    let primary = state.selection
    if (eqTarget(primary, target)) {
      primary = next.length > 0 ? next[next.length - 1] : null
    }
    set({ selection: primary, selectedTargets: next })
  },
  toggleSelection: (target) => {
    const state = get()
    const exists = state.selectedTargets.some((t) => eqTarget(t, target))
    if (exists) {
      // Inline removeFromSelection logic.
      const next = state.selectedTargets.filter((t) => !eqTarget(t, target))
      let primary = state.selection
      if (eqTarget(primary, target)) {
        primary = next.length > 0 ? next[next.length - 1] : null
      }
      set({ selection: primary, selectedTargets: next })
    } else {
      // Inline addToSelection logic.
      const next = [...state.selectedTargets, target]
      const cur = state.selectionHistory[state.historyIndex] ?? null
      if (eqTarget(target, cur)) {
        set({ selection: target, selectedTargets: next })
        return
      }
      const truncated = state.selectionHistory.slice(0, state.historyIndex + 1)
      truncated.push(target)
      set({
        selection: target,
        selectedTargets: next,
        selectionHistory: truncated,
        historyIndex: truncated.length - 1,
      })
    }
  },
  applyBulkSelection: (targets, mode) => {
    const state = get()
    const dedupedNew = dedupeTargets(targets)
    let next: SelectionTarget[]
    if (mode === 'replace') {
      next = dedupedNew
    } else if (mode === 'add') {
      const existingKeys = new Set(
        state.selectedTargets.map((t) => `${t.modelId}:${t.localId}`),
      )
      next = [...state.selectedTargets]
      for (const t of dedupedNew) {
        const key = `${t.modelId}:${t.localId}`
        if (!existingKeys.has(key)) {
          existingKeys.add(key)
          next.push(t)
        }
      }
    } else {
      const removeKeys = new Set(
        dedupedNew.map((t) => `${t.modelId}:${t.localId}`),
      )
      next = state.selectedTargets.filter(
        (t) => !removeKeys.has(`${t.modelId}:${t.localId}`),
      )
    }
    const primary = next.length > 0 ? next[next.length - 1] : null
    if (primary && !eqTarget(primary, state.selection)) {
      const cur = state.selectionHistory[state.historyIndex] ?? null
      if (!eqTarget(primary, cur)) {
        const truncated = state.selectionHistory.slice(
          0,
          state.historyIndex + 1,
        )
        truncated.push(primary)
        set({
          selection: primary,
          selectedTargets: next,
          selectionHistory: truncated,
          historyIndex: truncated.length - 1,
        })
        return
      }
    }
    set({ selection: primary, selectedTargets: next })
  },
  clearSelection: () => set({ selection: null, selectedTargets: [] }),
  setHover: (target) => set({ hover: target }),
  setHoverCursor: (cursor) => set({ hoverCursor: cursor }),
  setMarqueeMode: (v) =>
    set((state) => ({
      marqueeMode: v,
      // Drop hover when entering marquee mode.
      hover: v ? null : state.hover,
    })),
  setRightSidebarWidth: (width) =>
    set({
      rightSidebarWidth: Math.min(
        RIGHT_SIDEBAR_MAX,
        Math.max(RIGHT_SIDEBAR_MIN, width),
      ),
    }),
  setRightSidebarCollapsed: (collapsed) => {
    try {
      localStorage.setItem(RSB_COLLAPSED_KEY, String(collapsed))
    } catch {
      /* ignore */
    }
    set({ rightSidebarCollapsed: collapsed })
  },
  setLeftSidebarWidth: (width) => {
    const clamped = Math.min(
      LEFT_SIDEBAR_MAX,
      Math.max(LEFT_SIDEBAR_MIN, width),
    )
    try {
      localStorage.setItem(LSB_WIDTH_KEY, String(clamped))
    } catch {
      /* ignore */
    }
    set({ leftSidebarWidth: clamped })
  },
  setLeftSidebarCollapsed: (collapsed) => {
    try {
      localStorage.setItem(LSB_COLLAPSED_KEY, String(collapsed))
    } catch {
      /* ignore */
    }
    set({ leftSidebarCollapsed: collapsed })
  },
  setRawIfcEnabled: (v) => {
    persistRawIfcEnabled(v)
    set({ rawIfcEnabled: v })
  },
  goBackSelection: () => {
    const state = get()
    if (state.historyIndex <= 0) return null
    const newIndex = state.historyIndex - 1
    const target = state.selectionHistory[newIndex]
    set({ selection: target, historyIndex: newIndex })
    return target
  },
  goForwardSelection: () => {
    const state = get()
    if (state.historyIndex >= state.selectionHistory.length - 1) return null
    const newIndex = state.historyIndex + 1
    const target = state.selectionHistory[newIndex]
    set({ selection: target, historyIndex: newIndex })
    return target
  },
  clearSelectionHistory: () =>
    set({
      selectionHistory: [],
      historyIndex: -1,
      selection: null,
      selectedTargets: [],
    }),
  setRightSidebarMode: (mode) => {
    persistMode(mode)
    set({ rightSidebarMode: mode })
  },
  setRightSidebarFloating: (rect) => {
    persistFloating(rect)
    set({ rightSidebarFloating: rect })
  },
  openContextMenu: (state) => set({ contextMenu: state }),
  closeContextMenu: () => set({ contextMenu: null }),
})
